import axios from 'axios';
import { getSignedUrlFromCache } from './imageCache';
import {
  decodeJsonArrayBuffer,
  decodePdfServerResponse,
  PdfServerResponse,
  responseIsJson
} from './pdfResponse';
import {
  BookIdentityReadiness,
  CharacterReferenceView,
  QuickCreateState,
  ReadingAge,
  StructuredPlotData
} from '../types/storybook';
import { GenerationJob } from '../types/generationJob';
import { BookReviewSnapshot, ReviewHistoryTarget, ReviewLayer } from '../types/bookReview';
import {
  ExportLayoutOverrideAcknowledgement,
  ExportPreflightReport,
  ExportType
} from '../types/exportPreflight';

// In development with proxy, use relative URLs
// In production, use the full API URL from environment
const API_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || 'http://localhost:5001/api')
  : '/api'; // Proxy will forward to backend

// For static files (images), we need the full backend URL even in development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
const BASE_URL = API_URL.replace('/api', '');
const OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/i;
const PAID_LEGACY_AI_PATHS = new Set([
  '/ai/suggest-text',
  '/ai/rephrase-text',
  '/ai/generate-plot',
  '/ai/generate-quick-create-outline',
  '/ai/generate-single-page-plot',
  '/ai/generate-page-layout',
  '/ai/analyze-scene-layout',
  '/ai/rephrase-plot',
  '/ai/upscale-image',
  '/ai/edit-image',
  '/ai/apply-transparency'
]);

export const requiresPaidAiIdempotency = (
  requestUrl: string | undefined,
  method: string | undefined
): boolean => {
  if ((method || 'get').toLowerCase() !== 'post') return false;
  const requestPath = (requestUrl || '').split(/[?#]/, 1)[0];
  return PAID_LEGACY_AI_PATHS.has(requestPath);
};

export const createPaidAiIdempotencyKey = (): string => {
  const browserCrypto = typeof globalThis === 'undefined' ? undefined : globalThis.crypto;
  if (typeof browserCrypto?.randomUUID === 'function') {
    return `legacy-ai-${browserCrypto.randomUUID()}`;
  }
  // Compatibility fallback for older embedded browsers. Entropy does not
  // authorize anything; uniqueness is the only requirement here.
  return `legacy-ai-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

export const ensurePaidAiIdempotencyHeader = (
  headers: any,
  requestUrl: string | undefined,
  method: string | undefined,
  keyFactory: () => string = createPaidAiIdempotencyKey
): void => {
  if (!requiresPaidAiIdempotency(requestUrl, method)) return;
  const existingKey = typeof headers?.get === 'function'
    ? headers.get('Idempotency-Key')
    : headers?.['Idempotency-Key'] || headers?.['idempotency-key'];
  if (existingKey) return;
  if (typeof headers?.set === 'function') {
    headers.set('Idempotency-Key', keyFactory());
  } else {
    headers['Idempotency-Key'] = keyFactory();
  }
};

export const editorStorybookIdFromPathname = (pathname: string): string | null => {
  const match = pathname.match(/^\/editor\/([^/]+)\/?$/);
  if (!match) return null;
  try {
    const decoded = decodeURIComponent(match[1]);
    return OBJECT_ID_PATTERN.test(decoded) ? decoded : null;
  } catch {
    return null;
  }
};

export const storybookIdForAiRequest = (
  requestUrl: string | undefined,
  pathname: string
): string | null => {
  const requestPath = (requestUrl || '').split(/[?#]/, 1)[0];
  if (!/^\/ai(?:\/|$)/.test(requestPath)) return null;
  return editorStorybookIdFromPathname(pathname);
};

// Log API URL on startup for debugging
console.log('🔧 API Configuration:', {
  NODE_ENV: process.env.NODE_ENV,
  API_URL,
  BACKEND_URL,
  BASE_URL,
  env: process.env.REACT_APP_API_URL
});

/**
 * Convert server path to full URL
 * @param path - Server path like /uploads/characters/... or GCS URL
 * @returns Full URL like http://localhost:5001/uploads/characters/... or proxy URL
 */
export const getImageUrl = (path: string): string => {
  if (!path) return '';
  const authenticatedProxyUrl = (source: string): string => {
    const storybookId = typeof window === 'undefined'
      ? null
      : editorStorybookIdFromPathname(window.location.pathname);
    const query = new URLSearchParams({ url: source });
    if (storybookId) query.set('storybookId', storybookId);
    return `${API_URL}/storybooks/image-proxy?${query.toString()}`;
  };
  // If it's already a full URL, return as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    // Cloud objects are private. Prefer a cached short-lived signed URL, then
    // fall back to the authenticated ownership-checked proxy.
    if (path.startsWith('https://storage.googleapis.com/')) {
      // Check for cached signed URL first (in case it was pre-fetched)
      const cached = getSignedUrlFromCache(path);
      if (cached) {
        return cached;
      }
      return authenticatedProxyUrl(path);
    }
    return path;
  }
  // If it's a data URL, return as-is
  if (path.startsWith('data:')) {
    return path;
  }
  if (path.startsWith('/uploads/')) return authenticatedProxyUrl(path);
  // It's another server path, prepend backend URL.
  return `${BACKEND_URL}${path}`;
};

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const storybookId = storybookIdForAiRequest(
      config.url,
      typeof window === 'undefined' ? '' : window.location.pathname
    );
    if (storybookId) {
      config.headers['X-Storybook-Id'] = storybookId;
    }
    ensurePaidAiIdempotencyHeader(config.headers, config.url, config.method);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Check if error is 401 Unauthorized (token expired or invalid)
    if (error.response && error.response.status === 401) {
      // Clear stored auth data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Redirect to login page
      window.location.href = '/login?expired=true';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  register: async (email: string, password: string, name: string, couponCode?: string) => {
    const response = await api.post('/auth/register', { email, password, name, couponCode });
    return response.data;
  },
  googleLogin: async (credential: string) => {
    const response = await api.post('/auth/google', { credential });
    return response.data;
  },
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  }
};

// Storybook API
export const storybookAPI = {
  getAll: async () => {
    const response = await api.get('/storybooks');
    return response.data;
  },
  getById: async (id: string) => {
    const response = await api.get(`/storybooks/${id}`);
    return response.data;
  },
  create: async (data: {
    title: string;
    description?: string;
    plot?: string;
    canvasSize?: string;
    canvasWidth?: number;
    canvasHeight?: number;
    pageCount?: number;
    storyType?: string;
    storyTone?: string;
    drawingType?: string;
    drawingStyle?: string;
    renderingQuality?: string;
    referenceBook?: string;
    storyStylePrompt?: string;
    visualStylePrompt?: string;
    useReferenceBook?: boolean;
    seriesTemplateId?: 'tiny-little-fox-sequel-v1';
    pages?: any[];
    quickCreate?: QuickCreateState;
  }) => {
    const response = await api.post('/storybooks', data);
    return response.data;
  },
  update: async (id: string, data: any, signal?: AbortSignal) => {
    const response = await api.put(`/storybooks/${id}`, data, { signal });
    // Legacy editor callers often retain the object they submitted. Copy only
    // server-owned concurrency metadata back so their next save is not stale.
    if (data && response.data?.storybook) {
      data.revision = response.data.storybook.revision;
      data.generationSourceRevision = response.data.storybook.generationSourceRevision;
      if (Array.isArray(data.pages) && Array.isArray(response.data.storybook.pages)) {
        data.pages.forEach((page: any) => {
          const saved = response.data.storybook.pages.find((candidate: any) => candidate.id === page.id);
          if (saved) {
            page.revision = saved.revision;
            page.generationOperationKey = saved.generationOperationKey;
            page.generationOperationIds = saved.generationOperationIds;
            page.generationFence = saved.generationFence;
          }
        });
      }
    }
    return response.data;
  },
  updateQuickCreate: async (id: string, data: {
    quickCreate: QuickCreateState;
    structuredPlotData?: StructuredPlotData;
    clearStructuredPlotData?: boolean;
    reviewPreflight?: { expectedRevision: number; expectedInputHash: string };
  }) => {
    const response = await api.put(`/storybooks/${id}/quick-create`, data);
    return response.data;
  },
  initializeIdentity: async (id: string) => {
    const response = await api.post(`/storybooks/${id}/identity/initialize`);
    return response.data as { success: true; storybook: any; readiness: BookIdentityReadiness };
  },
  getIdentityReadiness: async (id: string) => {
    const response = await api.get(`/storybooks/${id}/identity/readiness`);
    return response.data as { success: true; readiness: BookIdentityReadiness };
  },
  createArtBibleVersion: async (id: string, data: {
    medium: string;
    palette: string[];
    lineStyle: string;
    lighting: string;
    texture: string;
    negativeStyle?: string[];
  }) => {
    const response = await api.post(`/storybooks/${id}/art-bible/versions`, data);
    return response.data;
  },
  approveArtBibleVersion: async (id: string, versionId: string) => {
    const response = await api.post(`/storybooks/${id}/art-bible/versions/${encodeURIComponent(versionId)}/approve`);
    return response.data;
  },
  createCharacterBibleVersion: async (id: string, characterId: string, data: {
    species: string;
    appearanceTraits: string[];
    relativeHeight: 'tiny' | 'small' | 'medium' | 'tall' | 'very-tall';
    clothing?: string[];
    accessories?: string[];
    forbiddenChanges?: string[];
  }) => {
    const response = await api.post(
      `/storybooks/${id}/characters/${encodeURIComponent(characterId)}/bible-versions`,
      data
    );
    return response.data;
  },
  approveCharacterBibleVersion: async (id: string, characterId: string, versionId: string) => {
    const response = await api.post(
      `/storybooks/${id}/characters/${encodeURIComponent(characterId)}/bible-versions/${encodeURIComponent(versionId)}/approve`
    );
    return response.data;
  },
  createCharacterReferenceVersion: async (id: string, characterId: string, data: {
    bibleVersionId: string;
    backgroundMode: 'transparent' | 'neutral';
    promptVersion?: string;
    references: Array<{
      id?: string;
      assetId?: string;
      imageUrl: string;
      thumbnailUrl?: string;
      label: string;
      purpose: CharacterReferenceView;
    }>;
  }) => {
    const response = await api.post(
      `/storybooks/${id}/characters/${encodeURIComponent(characterId)}/reference-versions`,
      data
    );
    return response.data;
  },
  approveCharacterReferenceVersion: async (id: string, characterId: string, versionId: string) => {
    const response = await api.post(
      `/storybooks/${id}/characters/${encodeURIComponent(characterId)}/reference-versions/${encodeURIComponent(versionId)}/approve`
    );
    return response.data;
  },
  migrateGenerationProfile: async (id: string) => {
    const response = await api.post(`/storybooks/${id}/generation-profile/migrate`, {
      confirmFullRerender: true
    });
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/storybooks/${id}`);
    return response.data;
  },
  copy: async (id: string, data: { canvasSize?: string; title?: string }) => {
    const response = await api.post(`/storybooks/${id}/copy`, data);
    return response.data;
  },
  merge: async (data: { storybookId1: string; storybookId2: string; title?: string; canvasSize?: string; removeDuplicateCovers?: boolean }) => {
    const response = await api.post('/storybooks/merge', data);
    return response.data;
  },
  addCharacter: async (id: string, name: string, prompt: string, imageUrl?: string, thumbnailUrl?: string, heightCategory?: string) => {
    const response = await api.post(`/storybooks/${id}/characters`, {
      name,
      prompt,
      imageUrl,
      thumbnailUrl,
      heightCategory: heightCategory || 'medium'
    });
    return response.data;
  },
  updateCharacter: async (id: string, name: string, prompt: string, imageUrl?: string, thumbnailUrl?: string) => {
    const response = await api.put(`/storybooks/${id}/characters/${encodeURIComponent(name)}`, {
      prompt,
      imageUrl,
      thumbnailUrl
    });
    return response.data;
  },
  addAsset: async (id: string, name: string, prompt: string, imageUrl?: string, thumbnailUrl?: string) => {
    const response = await api.post(`/storybooks/${id}/assets`, {
      name,
      prompt,
      imageUrl,
      thumbnailUrl
    });
    return response.data;
  },
  addBackground: async (id: string, name: string, prompt: string, imageUrl?: string, thumbnailUrl?: string) => {
    const response = await api.post(`/storybooks/${id}/backgrounds`, {
      name,
      prompt,
      imageUrl,
      thumbnailUrl
    });
    return response.data;
  },
  updateBackground: async (id: string, name: string, prompt: string, imageUrl?: string, thumbnailUrl?: string) => {
    const response = await api.put(`/storybooks/${id}/backgrounds/${encodeURIComponent(name)}`, {
      prompt,
      imageUrl,
      thumbnailUrl
    });
    return response.data;
  },
  addScene: async (id: string, name: string, prompt: string, imageUrl?: string, thumbnailUrl?: string) => {
    const response = await api.post(`/storybooks/${id}/scenes`, {
      name,
      prompt,
      imageUrl,
      thumbnailUrl
    });
    return response.data;
  },
  // Server-side PDF generation using Puppeteer
  generatePdfServer: async (id: string, exportType: ExportType = 'full', bleed: boolean = false) => {
    console.log('[API] Requesting PDF generation', { exportType, bleed });
    try {
      const response = await api.post(
        `/storybooks/${id}/generate-pdf-server`,
        { exportType, bleed },
        { responseType: 'arraybuffer' }
      );
      const contentType = response.headers['content-type'];
      const responseData = decodePdfServerResponse(response.data, contentType) as PdfServerResponse;
      console.log('[API] Received response:', {
        status: response.status,
        contentType,
        isObject: typeof responseData === 'object' && responseData !== null,
        hasDownloadUrl: (responseData as any)?.downloadUrl !== undefined
      });
      
      // Check if response is JSON with downloadUrl (Cloud Storage mode)
      if ((responseData as any)?.downloadUrl) {
        console.log('[API] Cloud Storage mode - received download URL');
        return responseData; // Return the full response object with downloadUrl
      }
      
      // Otherwise, it's a direct PDF blob
      console.log('[API] Direct mode - received PDF blob');
      return responseData; // ArrayBuffer
    } catch (error: any) {
      const errorContentType = error.response?.headers?.['content-type'];
      if (responseIsJson(errorContentType)) {
        try {
          error.response.data = decodeJsonArrayBuffer(error.response.data);
        } catch (decodeError) {
          console.error('[API] Could not decode PDF error response', decodeError);
        }
      }

      // Check if this is a 409 Conflict (generation already in progress)
      if (error.response?.status === 409 && error.response?.data?.inProgress) {
        console.log('[API] PDF generation already in progress');
        throw Object.assign(new Error(
          error.response.data.message || 'PDF generation is already in progress'
        ), {
          inProgress: true,
          elapsedSeconds: error.response.data.elapsedSeconds
        });
      }
      throw error;
    }
  },
  runExportPreflight: async (
    id: string,
    exportType: ExportType,
    bleed: boolean,
    signal?: AbortSignal
  ): Promise<{
    success: boolean;
    report: ExportPreflightReport;
    layoutOverrideAllowed?: true;
    code?: string;
    error?: string;
  }> => {
    try {
      const response = await api.post(
        `/storybooks/${id}/export-preflight`,
        { exportType, bleed },
        { signal }
      );
      return response.data;
    } catch (error: any) {
      if ([409, 422].includes(error.response?.status) && error.response?.data?.report) {
        return error.response.data;
      }
      throw error;
    }
  },
  generateExportProof: async (
    id: string,
    report: ExportPreflightReport,
    bleed: boolean,
    signal?: AbortSignal,
    layoutOverride?: ExportLayoutOverrideAcknowledgement
  ) => {
    try {
      const response = await api.post(`/storybooks/${id}/export-proof`, {
        exportType: report.selection.exportType,
        bleed,
        expectedRevision: report.bookRevision,
        expectedInputHash: report.inputHash,
        ...(layoutOverride ? { layoutOverride } : {})
      }, { signal });
      return response.data;
    } catch (error: any) {
      if ([409, 422].includes(error.response?.status) && error.response?.data?.report) {
        error.exportReport = error.response.data.report;
      }
      throw error;
    }
  },
  // Move image between categories
  moveImage: async (id: string, fromType: string, toType: string, name: string) => {
    const response = await api.post(`/storybooks/${id}/move-image`, {
      fromType,
      toType,
      name
    });
    return response.data;
  }
};

export const bookReviewAPI = {
  get: async (storybookId: string): Promise<{ success: true; review: BookReviewSnapshot }> => {
    const response = await api.get(`/storybooks/${storybookId}/review`);
    return response.data;
  },
  setLayerLock: async (
    storybookId: string,
    pageId: string,
    layer: ReviewLayer,
    locked: boolean
  ) => {
    const response = await api.patch(`/storybooks/${storybookId}/review/pages/${encodeURIComponent(pageId)}/locks`, {
      lockedLayers: { [layer]: locked }
    });
    return response.data;
  },
  regenerateLayer: async (storybookId: string, pageId: string, layer: ReviewLayer) => {
    const response = await api.post(
      `/storybooks/${storybookId}/review/pages/${encodeURIComponent(pageId)}/regenerate`,
      { requestedLayers: [layer] },
      { headers: { 'Idempotency-Key': `review:${storybookId}:${pageId}:${layer}:${Date.now()}` } }
    );
    return response.data as { success: true; job: GenerationJob; created: boolean };
  },
  fixSafe: async (storybookId: string, pageIds: string[], expectedBookRevision: number) => {
    const response = await api.post(`/storybooks/${storybookId}/review/fix-safe`, {
      pageIds,
      expectedBookRevision
    });
    return response.data;
  },
  listRevisions: async (storybookId: string, target: ReviewHistoryTarget) => {
    const response = await api.get(`/storybooks/${storybookId}/review/revisions`, {
      params: { entityType: target.type, entityId: target.id, limit: 20 }
    });
    return response.data;
  },
  getRevision: async (storybookId: string, revisionId: string) => {
    const response = await api.get(`/storybooks/${storybookId}/review/revisions/${revisionId}`);
    return response.data;
  },
  restoreRevision: async (storybookId: string, revisionId: string, expectedBookRevision: number) => {
    const response = await api.post(`/storybooks/${storybookId}/review/revisions/${revisionId}/restore`, {
      expectedBookRevision
    });
    return response.data;
  }
};

// Durable full-book generation. The browser enqueues and observes these jobs;
// it never owns the page loop or provider calls.
export const generationJobAPI = {
  startDraft: async (storybookId: string, idempotencyKey: string): Promise<{ job: GenerationJob; created: boolean }> => {
    const response = await api.post(
      `/storybooks/${storybookId}/generation-jobs`,
      {},
      { headers: { 'Idempotency-Key': idempotencyKey } }
    );
    return response.data;
  },
  getLatest: async (storybookId: string): Promise<{ job: GenerationJob | null }> => {
    const response = await api.get(`/storybooks/${storybookId}/generation-jobs/latest`);
    return response.data;
  },
  get: async (storybookId: string, jobId: string): Promise<{ job: GenerationJob }> => {
    const response = await api.get(`/storybooks/${storybookId}/generation-jobs/${jobId}`);
    return response.data;
  },
  pause: async (storybookId: string, jobId: string): Promise<{ job: GenerationJob }> => {
    const response = await api.post(`/storybooks/${storybookId}/generation-jobs/${jobId}/pause`);
    return response.data;
  },
  resume: async (storybookId: string, jobId: string): Promise<{ job: GenerationJob }> => {
    const response = await api.post(`/storybooks/${storybookId}/generation-jobs/${jobId}/resume`);
    return response.data;
  },
  cancel: async (storybookId: string, jobId: string): Promise<{ job: GenerationJob }> => {
    const response = await api.post(`/storybooks/${storybookId}/generation-jobs/${jobId}/cancel`);
    return response.data;
  },
  retry: async (storybookId: string, jobId: string, pageIds?: string[]): Promise<{ job: GenerationJob }> => {
    const response = await api.post(`/storybooks/${storybookId}/generation-jobs/${jobId}/retry`, { pageIds });
    return response.data;
  }
};

// AI API
export const aiAPI = {
  generatePlot: async (data: {
    prompt: string;
    numPages: number;
    readingAge?: ReadingAge;
    storyType?: string;
    storyTone?: string;
    storyStylePrompt?: string;
    visualStylePrompt?: string;
    dialogueStyle?: 'prose' | 'script';
    drawingType?: string;
    drawingStyle?: string;
  }) => {
    const response = await api.post('/ai/generate-plot', data);
    return response.data;
  },
  generateQuickCreateOutline: async (data: {
    prompt: string;
    numPages: number;
    readingAge?: ReadingAge;
    storyType?: string;
    storyTone?: string;
    storyStylePrompt?: string;
    visualStylePrompt?: string;
    dialogueStyle?: 'prose' | 'script';
    drawingType?: string;
    drawingStyle?: string;
  }) => {
    const response = await api.post('/ai/generate-quick-create-outline', data);
    return response.data;
  },
  generateCharacter: async (
    prompt: string, 
    storyType?: string, 
    drawingType?: string, 
    drawingStyle?: string,
    storyTone?: string,
    referenceImages?: string[],
    visualStylePrompt?: string,
    model?: 'chatgpt' | 'gemini',
    backgroundOption?: 'transparent' | 'full-color',
    chromaKeyColor?: 'white' | 'green' | 'blue',
    styleReferenceImages?: string[],
    idempotencyKey?: string
  ) => {
    const response = await api.post('/ai/generate-character', {
      prompt,
      storyType,
      drawingType,
      drawingStyle,
      storyTone,
      referenceImages,
      visualStylePrompt,
      model,
      backgroundOption,
      chromaKeyColor,
      styleReferenceImages
    }, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined);
    return response.data;
  },
  generateBackground: async (
    prompt: string, 
    storyType?: string, 
    drawingType?: string, 
    drawingStyle?: string,
    storyTone?: string,
    referenceImages?: string[],
    visualStylePrompt?: string,
    model?: 'chatgpt' | 'gemini',
    canvasSize?: string,
    viewTransforms?: string[],
    styleReferenceImages?: string[],
    idempotencyKey?: string
  ) => {
    const response = await api.post('/ai/generate-background', { 
      prompt,
      storyType,
      drawingType,
      drawingStyle,
      storyTone,
      referenceImages,
      visualStylePrompt,
      model,
      canvasSize,
      viewTransform: viewTransforms, // Backend expects viewTransform (singular) but we send array
      styleReferenceImages
    }, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined);
    return response.data;
  },
  composeScene: async (
    prompt: string, 
    characterImages: string[], 
    backgroundImage?: string,
    storyType?: string,
    drawingType?: string,
    drawingStyle?: string,
    storyTone?: string,
    canvasSize?: string,
    visualStylePrompt?: string,
    backgroundOption?: 'transparent' | 'full-color',
    chromaKeyColor?: 'white' | 'green' | 'blue',
    model?: 'chatgpt' | 'gemini',
    characterNames?: string[],
    characterHeights?: string[],
    assetImages?: string[],
    assetNames?: string[],
    aspectRatio?: '1:1' | '3:4' | '4:3' | '9:16' | '16:9',
    idempotencyKey?: string
  ) => {
    const response = await api.post('/ai/compose-scene', { 
      prompt,
      characterImages,
      characterNames,
      characterHeights,
      assetImages,
      assetNames,
      backgroundImage,
      storyType,
      drawingType,
      drawingStyle,
      storyTone,
      canvasSize,
      visualStylePrompt,
      backgroundOption,
      chromaKeyColor,
      model,
      aspectRatio
    }, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined);
    return response.data;
  },
  generateScene: async (
    prompt: string, 
    characterImages?: string[], 
    backgroundImage?: string, 
    storyType?: string,
    drawingType?: string,
    storyTone?: string
  ) => {
    const response = await api.post('/ai/generate-scene', { 
      prompt,
      characterImages,
      backgroundImage,
      storyType,
      drawingType,
      storyTone
    });
    return response.data;
  },
  suggestText: async (context: string, maxLength?: number) => {
    const response = await api.post('/ai/suggest-text', { context, maxLength });
    return response.data;
  },
  rephraseText: async (text: string, storyType: string, storyTone: string, storyStylePrompt?: string) => {
    const response = await api.post('/ai/rephrase-text', { text, storyType, storyTone, storyStylePrompt });
    return response.data;
  },
  rephraseTextOptions: async (
    text: string,
    storyType: string,
    storyTone: string,
    storyStylePrompt?: string,
    optionCount: number = 5
  ) => {
    const response = await api.post('/ai/rephrase-text', {
      text,
      storyType,
      storyTone,
      storyStylePrompt,
      optionCount
    });
    return response.data;
  },
  generateAsset: async (prompt: string, drawingType?: string, drawingStyle?: string, referenceImages?: string[], visualStylePrompt?: string, model?: 'chatgpt' | 'gemini', backgroundOption?: 'transparent' | 'full-color', chromaKeyColor?: 'white' | 'green' | 'blue', idempotencyKey?: string) => {
    const response = await api.post('/ai/generate-asset', { 
      prompt,
      drawingType,
      drawingStyle,
      referenceImages: [],
      styleReferenceImages: referenceImages,
      visualStylePrompt,
      model,
      backgroundOption,
      chromaKeyColor
    }, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined);
    return response.data;
  },
  editImage: async (imageUrl: string, prompt: string, storyType?: string, drawingType?: string, storyTone?: string, backgroundOption?: 'transparent' | 'full-color', chromaKeyColor?: 'white' | 'green' | 'blue', maskImageUrl?: string, lassoData?: { points: {x: number, y: number}[], mode: string, canvasWidth: number, canvasHeight: number }, referenceImages?: string[], aspectRatio?: string, cameraAngle?: string) => {
    const response = await api.post('/ai/edit-image', {
      imageUrl,
      prompt,
      storyType,
      drawingType,
      storyTone,
      backgroundOption,
      chromaKeyColor,
      maskImageUrl,
      lassoData,
      referenceImages,
      aspectRatio,
      cameraAngle
    });
    return response.data;
  },
  generateBookPrompts: async (bookName: string) => {
    const response = await api.post('/ai/generate-book-prompts', { bookName });
    return response.data;
  },
  applyTransparency: async (imageUrl: string, tolerance?: number, chromaKeyColor?: any, imageCategory?: 'asset' | 'character' | 'background' | 'scene', storybookId?: string, originalName?: string) => {
    const response = await api.post('/ai/apply-transparency', {
      imageUrl,
      tolerance,
      chromaKeyColor,
      imageCategory,
      storybookId,
      originalName
    });
    return response.data;
  },
  analyzeChromaColor: async (imageUrl: string) => {
    const response = await api.post('/ai/analyze-chroma-color', {
      imageUrl
    });
    return response.data;
  },
  generatePageLayout: async (
    canvasSize: { width: number; height: number },
    scenePrompt: string,
    textBoxes: Array<{ type: string; character?: string; content: string }>,
    backgroundDescription?: string,
    characterDescriptions?: Array<{ name: string; description: string }>
  ) => {
    const response = await api.post('/ai/generate-page-layout', {
      canvasSize,
      scenePrompt,
      textBoxes,
      backgroundDescription,
      characterDescriptions
    });
    return response.data;
  },

  analyzeSceneLayout: async (
    sceneImageUrl: string,
    canvasSize: { width: number; height: number },
    textBoxes: Array<{ type: string; character?: string; content: string }>,
    imageBounds?: { x: number; y: number; width: number; height: number }
  ) => {
    const response = await api.post('/ai/analyze-scene-layout', {
      sceneImageUrl,
      canvasSize,
      textBoxes,
      imageBounds
    });
    return response.data;
  },

  resizeImageToCanvas: async (
    imageUrl: string,
    canvasWidth: number,
    canvasHeight: number
  ) => {
    const response = await api.post('/ai/resize-image-to-canvas', {
      imageUrl,
      canvasWidth,
      canvasHeight
    });
    return response.data;
  },

  generateSinglePagePlot: async (
    prompt: string,
    pageNumber: number,
    options?: {
      storyType?: string;
      storyTone?: string;
      storyStylePrompt?: string;
      drawingType?: string;
      drawingStyle?: string;
      existingCharacters?: Array<{ name: string; description?: string }>;
      existingBackgrounds?: Array<{ name: string; description?: string }>;
    }
  ) => {
    const response = await api.post('/ai/generate-single-page-plot', {
      prompt,
      pageNumber,
      ...options
    });
    return response.data;
  },

  upscaleImage: async (
    imageUrl: string,
    upscaleFactor: 'x2' | 'x3' | 'x4',
    storybookId?: string
  ) => {
    const response = await api.post('/ai/upscale-image', {
      imageUrl,
      upscaleFactor,
      storybookId
    });
    return response.data;
  },

  checkImageResolution: async (imageUrl: string) => {
    const response = await api.post('/ai/check-image-resolution', {
      imageUrl
    });
    return response.data;
  }
};

// User API
export const userAPI = {
  getProfile: async () => {
    const response = await api.get('/user/profile');
    return response.data;
  },
  updateProfile: async (data: { name?: string; billingInfo?: any; customDrawingStyles?: string[] }) => {
    const response = await api.put('/user/profile', data);
    return response.data;
  },
  purchaseTokens: async (packageType: string, couponCode?: string) => {
    const response = await api.post('/user/purchase-tokens', { packageType, couponCode });
    return response.data;
  },
  validateCoupon: async (code: string) => {
    const response = await api.post('/user/validate-coupon', { code });
    return response.data;
  },
  getLedger: async (limit?: number) => {
    const response = await api.get('/user/ledger', { params: { limit } });
    return response.data;
  },
  getBillingHistory: async (limit?: number) => {
    const response = await api.get('/user/billing-history', { params: { limit } });
    return response.data;
  },
  getFiles: async () => {
    const response = await api.get('/user/files');
    return response.data;
  },
  recalculateStorage: async () => {
    const response = await api.post('/user/recalculate-storage');
    return response.data;
  },
  deleteFiles: async (fileUrls: string[]) => {
    const response = await api.post('/user/delete-files', { fileUrls });
    return response.data;
  },
  getTiers: async () => {
    const response = await api.get('/user/tiers');
    return response.data;
  }
};

export const paymentAPI = {
  validatePromoCode: async (code: string) => {
    const response = await api.post('/payment/validate-promo-code', { code });
    return response.data;
  },
  createCheckoutSession: async (packageType: string, couponCode?: string) => {
    const response = await api.post('/payment/create-checkout-session', {
      packageType,
      couponCode
    });
    return response.data;
  },
  createSubscription: async (tier: string) => {
    const response = await api.post('/payment/create-subscription', { tier });
    return response.data;
  },
  changeSubscription: async (tier: string) => {
    const response = await api.post('/payment/change-subscription', { tier });
    return response.data;
  },
  cancelSubscription: async () => {
    const response = await api.post('/payment/cancel-subscription');
    return response.data;
  },
  getPortalUrl: async () => {
    const response = await api.get('/payment/portal');
    return response.data;
  },
  cancelSession: async (sessionId: string) => {
    const response = await api.post('/payment/cancel-session', { sessionId });
    return response.data;
  },
  cancelTransaction: async (transactionId: string) => {
    const response = await api.post('/payment/cancel-transaction', { transactionId });
    return response.data;
  },
  verifySession: async (sessionId: string) => {
    const response = await api.get(`/payment/verify-session/${sessionId}`);
    return response.data;
  },
  getConfig: async () => {
    const response = await api.get('/payment/config');
    return response.data;
  }
};

// Admin API
export const adminAPI = {
  getUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },
  getCoupons: async () => {
    const response = await api.get('/admin/coupons');
    return response.data;
  },
  createCoupon: async (data: { code: string; discountPercent: number; usageLimit: number; expiresAt?: string }) => {
    const response = await api.post('/admin/coupons', data);
    return response.data;
  },
  deleteCoupon: async (id: string) => {
    const response = await api.get('/admin/coupons');
    return response.data;
  },
  toggleUserApiAccess: async (userId: string) => {
    const response = await api.patch(`/admin/users/${userId}/api-access`);
    return response.data;
  },
  toggleUserAdmin: async (userId: string) => {
    const response = await api.patch(`/admin/users/${userId}/admin`);
    return response.data;
  },
  lockUser: async (userId: string) => {
    const response = await api.patch(`/admin/users/${userId}/lock`);
    return response.data;
  },
  unlockUser: async (userId: string) => {
    const response = await api.patch(`/admin/users/${userId}/unlock`);
    return response.data;
  },
  getUserTransactions: async (userId: string, page?: number, limit?: number, type?: string) => {
    const response = await api.get(`/admin/users/${userId}/transactions`, {
      params: { page, limit, type }
    });
    return response.data;
  },
  getUserFiles: async (userId: string) => {
    const response = await api.get(`/admin/user-files/${userId}`);
    return response.data;
  },
  recalculateUserStorage: async (userId: string) => {
    const response = await api.post(`/admin/recalculate-storage/${userId}`);
    return response.data;
  }
};

// Image helpers
export const imageAPI = {
  // In-flight dedupe map to avoid duplicate concurrent requests for the same signed-url
  _pendingSignedUrlRequests: {} as { [key: string]: Promise<any> | undefined },
  getSignedUrl: async (gcsUrl: string) => {
    if (!gcsUrl) return null;
    // Return existing pending promise if present
    if (imageAPI._pendingSignedUrlRequests[gcsUrl]) {
      return imageAPI._pendingSignedUrlRequests[gcsUrl];
    }

    const promise = api.get('/storybooks/image-signed-url', { params: { url: gcsUrl } })
      .then(resp => {
        delete imageAPI._pendingSignedUrlRequests[gcsUrl];
        return resp.data;
      })
      .catch(err => {
        delete imageAPI._pendingSignedUrlRequests[gcsUrl];
        throw err;
      });

    imageAPI._pendingSignedUrlRequests[gcsUrl] = promise;
    return promise;
  }
};

export default api;
